<template>
  <RfDialog
    ref="dialog"
    width="550"
    :title="testerLabel"
    hideConfirm
    cancelText="Close"
    @close="emit('close')"
  >
    <div class="flex flex-col">
      <RfInput
        :modelValue="expression"
        readonly
        placeholder=""
        title="Your Expression"
        variant="outlined"
        class="mb-5 w-full"
      />
      <RfRadioGroup v-model="selectedExpression" :options="expressionOptions" />
      <RfInput
        v-model="testString"
        class="mt-4 w-full"
        variant="outlined"
        :title="`Your Test ${expressionTypes[selectedExpression].label}`"
        :placeholder="expressionTypes[selectedExpression].placeholder"
      />
      <div class="msg-note mt-2 flex items-center gap-1" style="height: 24px">
        <template v-if="matchResult">
          <RfSuccessIcon class="h-7 w-7 stroke-green-50" />
          <span class="success-msg-note text-green-50">Success</span>
        </template>
        <template v-else>
          <RfErrorIcon class="h-7 w-7 stroke-yellow-600" />
          <span class="warning-msg-note text-yellow-600">No Match</span>
        </template>
      </div>
    </div>
  </RfDialog>
</template>

<script setup>
import { computed, ref, useTemplateRef } from "vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfRadioGroup from "@/components/inputs/RfRadioGroup.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfSuccessIcon from "@/components/icons/RfSuccessIcon.vue";
import RfErrorIcon from "@/components/icons/RfErrorIcon.vue";

const props = defineProps({
  testType: { type: String, required: true },
  urlPath: { type: String, default: "" },
  useRegex: { type: Boolean, default: false },
  queryParams: { type: String, default: "" },
  urlHash: { type: String, default: "" },
  urlHost: { type: String, default: "" },
});

const emit = defineEmits(["close"]);

const dialog = useTemplateRef("dialog");
const testString = ref("");
const selectedExpression = ref("urlPath");
const expressionTypes = {
  urlPath: { label: "Url Path", placeholder: "/settings" },
  queryParams: { label: "Query Parameters", placeholder: "version=2020" },
  urlHash: { label: "Url Hash", placeholder: "#profile" },
  urlHost: { label: "Url Host", placeholder: "example.com" },
};

const expressionOptions = computed(() =>
  Object.entries(expressionTypes).map(([value, settings]) => ({ value, label: settings.label })),
);
const expression = computed(() => props[selectedExpression.value]);
const testerLabel = computed(() => `Test your ${props.testType} expression`);
const matchResult = computed(() => {
  let path = expression.value;
  if (!path) return null;
  try {
    if (!props.useRegex) {
      path = path.replace(/\*/g, ".*");
      path = `^${path}$`;
    }
    const regexp = new RegExp(path, "i");
    return regexp.exec(testString.value.trim());
  } catch {
    // Invalid regex from user input — surfaced to the user as "No Match" below.
  }
  return null;
});

const open = () => {
  testString.value = "";
  dialog.value?.open();
};
const close = () => {
  dialog.value?.close();
};

defineExpose({ open, close });
</script>
